import type { PatientModel } from '@ctw/shared/api/fhir/models/patient';
import {
  getBuilderFhirPatientByIdentifier,
  getPatientByID,
} from '@ctw/shared/api/fhir/patient-helper';
import {
  schedulePatientHistory,
  usePatientHistoryDetails,
} from '@ctw/shared/api/patient-history/patient-history';
import type { PatientHistoryResponseError } from '@ctw/shared/api/patient-history/patient-history';
import { DrawerFormWithFields } from '@ctw/shared/components/form/drawer-form-with-fields';
import { notify } from '@ctw/shared/components/toast';
import {
  getRequestData,
  requestHistorySchema,
} from '@ctw/shared/content/forms/schemas/request-history-schema';
import { type CTWRequestContext, type CTWState, useCTW } from '@ctw/shared/context/ctw-context';
import { useDrawer } from '@ctw/shared/context/drawer-context';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { QUERY_KEY_PATIENT_HISTORY_DETAILS } from '@ctw/shared/utils/query-keys';
import { useQueryClient } from '@tanstack/react-query';
import { type Dispatch, type SetStateAction, useCallback } from 'react';

export function usePatientHistory() {
  const telemetry = useTelemetry();
  const { openDrawer } = useDrawer({
    boundaryName: 'PatientHistoryDrawer',
    telemetryContext: { action: 'patient_history' },
  });
  const { getPatient } = usePatientPromise();
  const { requestContext } = useCTW();
  const details = usePatientHistoryDetails();

  return {
    openHistoryRequestDrawer: async () => {
      let patient: PatientModel | undefined;
      try {
        patient = await getPatient(requestContext);
      } catch {
        patient = undefined;
      }
      openDrawer({
        telemetry,
        action: {
          title: 'Request Records',
          subtitle:
            'Request records from outside provider networks. To enable automatic updates, reach out to your admin.',
          content: {
            body: (
              <PatientHistoryRequestDrawer
                setClinicalHistoryExists={() => {
                  /* noop */
                }}
                patient={patient}
              />
            ),
          },
        },
      });
    },
    isLoading: details.isLoading,
    isError: details.isError,
    details: details.data ?? {},
  };
}

function usePatientPromise() {
  const { patientResourceID, patientID, systemURL } = usePatientContext();
  const { ctwFetch } = useCTW();
  const telemetry = useTelemetry();

  const getPatient = useCallback(
    async (requestContext: CTWRequestContext) => {
      const patientIdUsed =
        systemURL && patientID ? `${systemURL}|${patientID}` : patientResourceID;
      try {
        let patient: PatientModel;
        if (systemURL && patientID) {
          patient = await getBuilderFhirPatientByIdentifier(
            ctwFetch,
            requestContext,
            patientID,
            systemURL,
          );
        } else if (patientResourceID) {
          patient = await getPatientByID(ctwFetch, requestContext, patientResourceID);
        } else {
          throw new Error(
            'Must specify a patient ID and system URL or a patient FHIR resource ID to retrieve a patient.',
          );
        }

        if (patient.active !== true) {
          // Assume null is inactive (different from the FHIR spec) since we use this property to control CPR access.
          void telemetry.logger.warn(`User accessing inactive patient: ${patientIdUsed}`);
        }

        return patient;
      } catch (e) {
        const msg = `Failed to get patient with ID ${patientIdUsed}`;
        telemetry.trackError({
          message: msg,
          error: e instanceof Error ? e : new Error(msg),
          context: {
            e,
          },
        });
        throw e;
      }
    },
    [systemURL, patientID, patientResourceID, ctwFetch, telemetry],
  );

  return { patientResourceID, patientID, systemURL, getPatient };
}

export type GetPatientHistoryJobsParams = {
  ctwFetch: CTWState['ctwFetch'];
  requestContext: CTWRequestContext;
  count?: number;
  offset?: number;
  patientId?: string;
  status?: string;
  excludeFutureJobs?: boolean;
};

type PatientHistoryRequestDrawer = {
  patient?: PatientModel;
  setClinicalHistoryExists: Dispatch<SetStateAction<boolean | undefined>>;
};

type ScheduleHistoryFormData = {
  npi: string;
  name: string;
  role: string;
  id: string;
};

const PatientHistoryRequestDrawer = ({
  patient,
  setClinicalHistoryExists,
}: PatientHistoryRequestDrawer) => {
  const queryClient = useQueryClient();
  const telemetry = useTelemetry();

  const onScheduleHistory = async (data: ScheduleHistoryFormData, ctw: CTWState) => {
    try {
      const patientHistoryResponse: Record<string, unknown> = (await schedulePatientHistory(
        ctw,
        data,
      )) as Record<string, unknown>;

      // TODO - this may not accurately reflect the error response from the patient history API endpoint
      if ('errors' in patientHistoryResponse && Array.isArray(patientHistoryResponse.errors)) {
        const requestErrors = [
          patientHistoryResponse.errors.map((err: PatientHistoryResponseError) => err.details),
        ];
        return new Error(requestErrors.join(', '));
      }

      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY_PATIENT_HISTORY_DETAILS] });

      // patientHistoryResponse has succeeded at this point and should remove empty request history state.
      setClinicalHistoryExists(true);

      notify({
        type: 'success',
        title: 'Successfully requested records refresh',
      });

      return patientHistoryResponse;
    } catch (error) {
      telemetry.trackError({ message: 'Error requesting patient history.', error });
      return error;
    }
  };

  return (
    <DrawerFormWithFields
      boundaryName="PatientHistoryRequestDrawer"
      errorHeader="Unable to request records"
      action={onScheduleHistory as never}
      data={getRequestData(patient, false)}
      schema={requestHistorySchema}
    />
  );
};
